import {
  DO_NOT_USE_platformMetrics,
  type IAnalytics,
  type IReports,
} from '@sail/observability';
import {DEFAULT_LOCALE} from 'src/internal/constants';
import createIntlWithLocale from 'src/internal/createIntlWithLocale';
import {handleReactIntlError} from 'src/internal/errors/handleReactIntlError/handleReactIntlError';

import type {FormattedMessage} from 'src/public/formatMessage/types';
import type {
  MessageDescriptor,
  UnionToIntersection,
  messageDescriptorUniqueIdentifierType,
} from 'src/public/message/types';
import type {IntlConfig} from 'src/types';

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-intl/api/useIntl#format-message
 */
export default function formatMessage<
  /** Generic type for the provided message descriptor map object */
  TMessage extends MessageDescriptor<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
  /** Generic type for the message values to be passed-in to the formatter  */
  TValues extends UnionToIntersection<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
>(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
  /** The message descriptor map object passed-in for formatting */
  descriptor: TMessage,
  /** The values allowed to be passed-in to the message */
  ...values: TValues extends Record<string, any>
    ? [UnionToIntersection<TValues>]
    : [never?]
): FormattedMessage<TValues> {
  const locale =
    descriptor?.translate === false ? DEFAULT_LOCALE : config.locale;
  try {
    const defaultMessage = descriptor?.defaultMessage;
    DO_NOT_USE_platformMetrics.trackTranslationCount('request');
    DO_NOT_USE_platformMetrics.translationCountForMessageLength(
      'request',
      defaultMessage?.length ?? 0,
    );
    const intl = createIntlWithLocale(locale, config, reports, analytics);

    return intl.formatMessage(
      {
        id: descriptor?.id,
        defaultMessage,
      },
      values[0],
    );
  } catch (error) {
    handleReactIntlError(reports, analytics, locale, error);

    return descriptor?.defaultMessage ?? descriptor?.id ?? '';
  }
}

export type FormatMessage = typeof formatMessage;

export type FormatMessageDeclarative = <
  TMessage extends MessageDescriptor<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
  TValues extends UnionToIntersection<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
>(
  descriptor: TMessage,
  ...values: TValues extends Record<string, any>
    ? [UnionToIntersection<TValues>]
    : [never?]
) => FormattedMessage<TValues>;
